import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import admissionsService from '../services/admissionsService';
import frontOfficeService from '../services/frontOfficeService';
import StaffChat from '../components/communication/StaffChat';
import {
  UserPlus, DoorOpen, ChartBar, User, Envelope, Briefcase, ChatCircle
} from '@phosphor-icons/react';

const ENROLLED_STATUSES = ['enrolled', 'rejected'];

function RegistrarPortalPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [pipelineCount, setPipelineCount] = useState(0);
  const [enrolledCount, setEnrolledCount] = useState(0);
  const [visitorsOnCampus, setVisitorsOnCampus] = useState(0);
  const [tab, setTab] = useState('dashboard');

  useEffect(() => {
    if (user && user.role !== 'registrar') {
      toast.error('You do not have access to this page');
      navigate('/');
    }
  }, [user, navigate]);

  useEffect(() => {
    if (!user?.school_id) return;

    Promise.all([
      admissionsService.listApplicants(),
      frontOfficeService.listVisitors('checked_in'),
    ])
      .then(([applicants, visitors]) => {
        const list = Array.isArray(applicants) ? applicants : [];
        setPipelineCount(list.filter(a => !ENROLLED_STATUSES.includes(a.status)).length);
        setEnrolledCount(list.filter(a => a.status === 'enrolled').length);
        setVisitorsOnCampus(Array.isArray(visitors) ? visitors.length : 0);
      })
      .catch(err => {
        console.error(err);
        toast.error('Failed to load registrar overview');
      })
      .finally(() => setLoading(false));
  }, [user]);

  if (loading) {
    return (
      <Layout>
        <div className="page">
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading registrar portal...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page">
        <div className="page-header">
          <div>
            <h1>Registrar Portal</h1>
            <p>Admissions pipeline and front office at a glance</p>
          </div>
          <div className="header-actions">
            <div className="user-info">
              <User size={20} />
              <span>{user?.first_name} {user?.last_name}</span>
            </div>
          </div>
        </div>

        <div className="tabs-container">
          <button className={`tab-btn ${tab === 'dashboard' ? 'active' : ''}`} onClick={() => setTab('dashboard')}>
            <Briefcase size={18} /> Dashboard
          </button>
          <button className={`tab-btn ${tab === 'messages' ? 'active' : ''}`} onClick={() => setTab('messages')}>
            <ChatCircle size={18} /> Messages
          </button>
        </div>

        {tab === 'messages' ? <StaffChat /> : (
        <>
        <div className="dashboard-grid">
          <div className="dashboard-card">
            <h3>My Profile</h3>
            <div className="profile-info">
              <div className="profile-item">
                <div className="profile-icon"><User size={20} /></div>
                <div className="profile-content">
                  <div className="profile-label">Name</div>
                  <div className="profile-value">{user?.first_name} {user?.last_name}</div>
                </div>
              </div>
              <div className="profile-item">
                <div className="profile-icon"><Envelope size={20} /></div>
                <div className="profile-content">
                  <div className="profile-label">Email</div>
                  <div className="profile-value">{user?.email}</div>
                </div>
              </div>
              <div className="profile-item">
                <div className="profile-icon"><Briefcase size={20} /></div>
                <div className="profile-content">
                  <div className="profile-label">Role</div>
                  <div className="profile-value">Registrar</div>
                </div>
              </div>
            </div>
          </div>

          <div className="dashboard-card">
            <h3>Admissions Pipeline</h3>
            <div className="stats-grid">
              <div className="stat">
                <div className="stat-value">{pipelineCount}</div>
                <div className="stat-label">In Progress</div>
              </div>
              <div className="stat">
                <div className="stat-value">{enrolledCount}</div>
                <div className="stat-label">Enrolled</div>
              </div>
            </div>
          </div>

          <div className="dashboard-card">
            <h3>Front Office</h3>
            <div className="stats-grid">
              <div className="stat">
                <div className="stat-value">{visitorsOnCampus}</div>
                <div className="stat-label">On Campus</div>
              </div>
            </div>
          </div>
        </div>

        <div className="quick-actions">
          <h3>Quick Actions</h3>
          <div className="actions-grid">
            <button className="action-btn" onClick={() => navigate('/admissions/applicants')}>
              <UserPlus size={24} />
              <span>Admissions</span>
            </button>
            <button className="action-btn" onClick={() => navigate('/front-office/visitors')}>
              <DoorOpen size={24} />
              <span>Front Office</span>
            </button>
            <button className="action-btn" onClick={() => navigate('/reports')}>
              <ChartBar size={24} />
              <span>Reports</span>
            </button>
          </div>
        </div>
        </>
        )}
      </div>
    </Layout>
  );
}

export default RegistrarPortalPage;
